// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The artefacts.
 *
 * The report is built entirely with `createElement` and `textContent` and then
 * serialised. Every value in it — a file name, a column name, a passage out of
 * a training corpus — comes from a file evalspill did not write, and the report
 * is opened from `file://`, where a script would run with no origin to
 * constrain it. String templating here would let a training corpus put script
 * into the document that reports on it.
 *
 * The CSV carries a commented REPRODUCIBILITY MANIFEST as its first lines. A
 * contamination percentage with no record of the n-gram size, the unit, the
 * normalisation and the boilerplate cap is uncitable the moment a reviewer asks
 * how it was computed — which is the first thing a reviewer asks.
 */

import { csvBlob } from './core/csv.ts';
import { percent, points } from './core/stats.ts';
import type { RunOptions, RunReport } from './types.ts';

export const TOOL_VERSION = '1.0.0';

export function manifestLines(report: RunReport, options: RunOptions): string[] {
  const lines = [
    `evalspill ${TOOL_VERSION} — https://evalspill.benrichardson.dev`,
    `n-gram size: ${report.ngramSize} words (${
      report.ngramSource === 'gpt3-rule'
        ? "chosen from this eval file's 5th-percentile row length, floor 8, ceiling 13"
        : 'set by hand'
    })`,
    'unit: words. normalisation: lowercase, punctuation removed, whitespace collapsed (GPT-3 Appendix C)',
    `metric: containment and longest match, both normalised by the EVAL row`,
    `eval fields compared: ${options.evalFields.join(' + ') || '(whole record)'}`,
    `training fields compared: ${options.trainFields.join(' + ') || '(whole record)'}`,
    report.documentFrequencyCap > 0
      ? `boilerplate filter: word runs occurring in more than ${report.documentFrequencyCap.toLocaleString()} distinct training records were ignored (${report.boilerplateNgrams.toLocaleString()} runs)`
      : 'boilerplate filter: OFF — no word run was ignored for being common',
    report.strippedPrefix || report.strippedSuffix
      ? `shared affixes stripped before comparison: prefix ${report.strippedPrefix.length} chars, suffix ${report.strippedSuffix.length} chars`
      : 'shared affixes stripped: none found',
    `eval records: ${report.evalRecords.toLocaleString()} (${report.evalScorable.toLocaleString()} long enough to score, ${report.evalTooShort.toLocaleString()} shorter than ${report.ngramSize} words and therefore not scored)`,
    `training records scanned: ${report.trainScanned.toLocaleString()}${report.trainSampled ? ' (SAMPLED — not the whole file)' : ''}`,
  ];
  return lines;
}

export function contaminationCsv(report: RunReport, options: RunOptions): Blob {
  const header = [
    'eval_record',
    'longest_match_words',
    'eval_words',
    'containment',
    'distinct_train_records',
    'score',
    'longest_shared_span',
    'eval_text',
    'train_excerpt',
  ];
  const rows = report.flagged.map((row) => [
    row.recordNumber,
    row.longestMatchWords,
    row.wordCount,
    row.containment.toFixed(4),
    row.distinctTrainRecords,
    row.score === null ? '' : row.score,
    row.text.slice(row.spanStart, row.spanEnd),
    row.text.length > 2000 ? `${row.text.slice(0, 2000)}…` : row.text,
    row.example ? row.example.excerpt : '',
  ]);
  return csvBlob(header, rows, manifestLines(report, options));
}

export function tooShortCsv(report: RunReport, options: RunOptions): Blob {
  return csvBlob(
    ['eval_record', 'words', 'eval_text'],
    report.tooShort.map((row) => [row.recordNumber, row.words, row.text]),
    [
      ...manifestLines(report, options),
      '',
      `These rows are shorter than ${report.ngramSize} words, so no ${report.ngramSize}-word run can be formed from them`,
      'and they were NOT checked. They are not evidence of a clean eval set; they are unmeasured.',
    ],
  );
}

/* ── the report ───────────────────────────────────────────────────────────── */

function el(tag: string, className?: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

const REPORT_STYLE = `
:root { color-scheme: light; }
body { font: 15px/1.6 -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
       margin: 0; padding: 2rem 1.25rem 4rem; color: #16202b; background: #fff; }
main { max-width: 60rem; margin: 0 auto; }
h1 { font-size: 1.4rem; margin: 0 0 .25rem; }
h2 { font-size: 1rem; margin: 2rem 0 .5rem; border-bottom: 1px solid #dbe3ec; padding-bottom: .3rem; }
p { margin: .4rem 0; }
.lede { color: #46586c; }
.figure { font-size: 1.6rem; font-weight: 650; margin: .3rem 0; }
.caveat { background: #fff8e6; border-left: 3px solid #b7791f; padding: .7rem .9rem; margin: 1rem 0; }
.manifest { background: #f4f7fa; border: 1px solid #dbe3ec; border-radius: 6px;
            padding: .8rem 1rem; font: 12px/1.7 "SF Mono", Consolas, monospace; overflow-x: auto; }
table { border-collapse: collapse; width: 100%; font-size: 13px; margin-top: .6rem; }
th, td { border-bottom: 1px solid #e4eaf1; padding: .4rem .5rem; text-align: left; vertical-align: top; }
th { color: #55697e; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
td.n { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
mark { background: #ffe9a8; padding: 0 .1em; }
.excerpt { color: #46586c; font-size: 12px; }
footer { margin-top: 2.5rem; color: #55697e; font-size: 12px; }
`;

export function buildReport(report: RunReport, options: RunOptions, trainName: string, evalName: string): Blob {
  const doc = document.implementation.createHTMLDocument('evalspill report');
  doc.documentElement.lang = 'en';

  const charset = doc.createElement('meta');
  charset.setAttribute('charset', 'utf-8');
  const viewport = doc.createElement('meta');
  viewport.setAttribute('name', 'viewport');
  viewport.setAttribute('content', 'width=device-width, initial-scale=1');
  const style = doc.createElement('style');
  style.textContent = REPORT_STYLE;
  doc.head.append(charset, viewport, style);
  doc.title = 'evalspill — overlap report';

  const main = doc.createElement('main');

  main.appendChild(el('h1', undefined, 'Train / eval overlap report'));
  main.appendChild(
    el(
      'p',
      'lede',
      `${evalName} checked against ${trainName}. Produced by evalspill ${TOOL_VERSION}; both files were read in the browser and neither was uploaded.`,
    ),
  );

  const share = report.evalScorable ? report.flaggedCount / report.evalScorable : 0;
  main.appendChild(el('h2', undefined, 'Result'));
  main.appendChild(
    el(
      'p',
      'figure',
      `${report.flaggedCount.toLocaleString()} of ${report.evalScorable.toLocaleString()} scorable eval rows (${percent(
        share,
      )}) contain a run of ${report.ngramSize} words that also appears in the training file.`,
    ),
  );
  if (report.evalTooShort > 0) {
    main.appendChild(
      el(
        'p',
        undefined,
        `A further ${report.evalTooShort.toLocaleString()} eval rows are shorter than ${report.ngramSize} words. No run of that length can be formed from them, so they were not checked. They are unmeasured, not clean.`,
      ),
    );
  }

  if (report.comparison) {
    const c = report.comparison;
    main.appendChild(el('h2', undefined, 'What this can do to the score'));
    main.appendChild(
      el(
        'p',
        undefined,
        `The eval set scores ${percent(c.overall)}. Removing the ${report.flaggedCount.toLocaleString()} overlapping rows moves that to somewhere between ${percent(
          c.bound.low,
        )} and ${percent(c.bound.high)} — at most ${points(c.bound.maxDrop)} points down.`,
      ),
    );
    if (c.withinNoise) {
      main.appendChild(
        el(
          'p',
          'caveat',
          `Measured directly, the clean subset scores ${percent(c.cleanMean)} (${percent(
            c.cleanInterval.low,
          )}–${percent(c.cleanInterval.high)}) and the overlapping subset ${percent(c.flaggedMean)} (${percent(
            c.flaggedInterval.low,
          )}–${percent(c.flaggedInterval.high)}). Those intervals overlap: this comparison does not separate them.`,
        ),
      );
    } else {
      main.appendChild(
        el(
          'p',
          undefined,
          `Measured directly, the clean subset scores ${percent(c.cleanMean)} (${percent(
            c.cleanInterval.low,
          )}–${percent(c.cleanInterval.high)}) against ${percent(c.flaggedMean)} (${percent(
            c.flaggedInterval.low,
          )}–${percent(c.flaggedInterval.high)}) on the overlapping rows.`,
        ),
      );
      main.appendChild(
        el(
          'p',
          'caveat',
          'The clean-subset score is a re-measurement, not a correction. It is the score on a smaller and different set of questions, which may be easier or harder for reasons unrelated to overlap.',
        ),
      );
    }
  }

  main.appendChild(el('h2', undefined, 'What this does not tell you'));
  const caveats = doc.createElement('ul');
  for (const line of [
    'It does not tell you a model cheated. It tells you that two files share text.',
    'An overlap is a fact about your files. Whether it inflated a score is a separate question this tool cannot answer.',
    'Finding nothing here does not mean your eval set is clean: the base model\'s pretraining data is not in this comparison, and for almost every model it is not public.',
    'A decontaminated eval set is a new eval set. Scores on it are not comparable to the scores you already have.',
    'Every number here is at the settings recorded below. Quoted without them, it means nothing.',
  ]) {
    const item = doc.createElement('li');
    item.textContent = line;
    caveats.appendChild(item);
  }
  main.appendChild(caveats);

  main.appendChild(el('h2', undefined, 'Settings'));
  const manifest = el('div', 'manifest');
  for (const line of manifestLines(report, options)) {
    const row = doc.createElement('div');
    row.textContent = line;
    manifest.appendChild(row);
  }
  main.appendChild(manifest);

  main.appendChild(el('h2', undefined, `Overlapping rows (${Math.min(report.flagged.length, 500)} shown)`));
  const table = doc.createElement('table');
  const thead = doc.createElement('thead');
  const headRow = doc.createElement('tr');
  for (const label of ['Row', 'Longest match', 'Containment', 'Train rows', 'The eval row, with the shared passage marked']) {
    const th = doc.createElement('th');
    th.textContent = label;
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = doc.createElement('tbody');
  for (const row of report.flagged.slice(0, 500)) {
    const tr = doc.createElement('tr');

    const number = doc.createElement('td');
    number.className = 'n';
    number.textContent = String(row.recordNumber);

    const longest = doc.createElement('td');
    longest.className = 'n';
    longest.textContent = `${row.longestMatchWords} w`;

    const containment = doc.createElement('td');
    containment.className = 'n';
    containment.textContent = percent(row.containment);

    const trainCount = doc.createElement('td');
    trainCount.className = 'n';
    trainCount.textContent = String(row.distinctTrainRecords);

    // The shared passage is marked by SPLITTING the text and creating a <mark>,
    // never by building markup out of it.
    const text = doc.createElement('td');
    const clipped = row.text.length > 1200 ? `${row.text.slice(0, 1200)}…` : row.text;
    const spanStart = Math.min(row.spanStart, clipped.length);
    const spanEnd = Math.min(row.spanEnd, clipped.length);
    text.appendChild(doc.createTextNode(clipped.slice(0, spanStart)));
    const mark = doc.createElement('mark');
    mark.textContent = clipped.slice(spanStart, spanEnd);
    text.appendChild(mark);
    text.appendChild(doc.createTextNode(clipped.slice(spanEnd)));

    if (row.example) {
      const excerpt = doc.createElement('div');
      excerpt.className = 'excerpt';
      excerpt.textContent = `in training record ${row.example.trainRecord + 1}: ${row.example.excerpt}`;
      text.appendChild(excerpt);
    }

    tr.append(number, longest, containment, trainCount, text);
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  main.appendChild(table);

  const footer = doc.createElement('footer');
  footer.textContent =
    'evalspill runs entirely in the browser. This report was generated on your machine from files that were never uploaded.';
  main.appendChild(footer);

  doc.body.appendChild(main);

  // A READ of a document built entirely from createElement and textContent.
  return new Blob([`<!doctype html>\n${doc.documentElement.outerHTML}`], { type: 'text/html;charset=utf-8' });
}

/**
 * Download a blob.
 *
 * Triggered synchronously inside the click handler: Chromium flips a tab to
 * "ask before downloading" after the first programmatic download, and a missed
 * user gesture means the second artefact silently never arrives.
 */
export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
